/**
 * Every widget renders inside its own shadow root, so the host page's CSS never
 * reaches these rules and these rules never leak out. The trade-off is that
 * nothing is inherited either — hence an explicit, self-contained base sheet.
 *
 * Colours are CSS custom properties. Custom properties *do* pierce the shadow
 * boundary, so a host site restyles a widget by setting `--sports-*` on the
 * element or any ancestor, without us exposing internals via ::part.
 */
export const baseStyles = `
  :host {
    /* Palette adopted from the betbrothers v2 hello-widget: navy header, light
       card surface, red accent, green live. Tokens are HSL triplets so a host
       page can retune any of them. */
    --sports-font: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    --h-bg: 0 0% 96%;
    --h-fg: 225 15% 20%;
    --h-header: 226 65% 24%;
    --h-header-fg: 0 0% 100%;
    --h-card: 0 0% 100%;
    --h-muted: 220 14% 95%;
    --h-muted-fg: 225 8% 45%;
    --h-border: 220 13% 88%;
    --h-accent: 0 60% 55%;
    --h-live: 0 72% 51%;
    --h-success: 142 70% 40%;

    --sports-bg: hsl(var(--h-bg));
    --sports-card: hsl(var(--h-card));
    --sports-fg: hsl(var(--h-fg));
    --sports-muted: hsl(var(--h-muted-fg));
    --sports-muted-bg: hsl(var(--h-muted));
    --sports-border: hsl(var(--h-border));
    --sports-accent: hsl(var(--h-success));
    --sports-live: hsl(var(--h-live));
    --sports-header: hsl(var(--h-header));
    --sports-header-fg: hsl(var(--h-header-fg));
    --sports-radius: 0.5rem;

    display: block;
    font-family: var(--sports-font);
    color: var(--sports-fg);
    background: transparent;
    box-sizing: border-box;
    container-type: inline-size;
  }

  /* Dark theme — the hello-widget football24 palette. */
  :host([theme="dark"]),
  :host([theme="football24"]) {
    --h-bg: 220 20% 10%;
    --h-fg: 0 0% 95%;
    --h-header: 220 20% 8%;
    --h-card: 220 18% 13%;
    --h-muted: 220 15% 18%;
    --h-muted-fg: 220 10% 62%;
    --h-border: 220 15% 20%;
    --h-success: 145 70% 45%;
  }

  @media (prefers-color-scheme: dark) {
    :host(:not([theme="light"]):not([theme="football24"])) {
      --h-bg: 220 20% 10%;
      --h-fg: 0 0% 95%;
      --h-header: 220 20% 8%;
      --h-card: 220 18% 13%;
      --h-muted: 220 15% 18%;
      --h-muted-fg: 220 10% 62%;
      --h-border: 220 15% 20%;
      --h-success: 145 70% 45%;
    }
  }

  :host([hidden]) { display: none; }

  * { box-sizing: inherit; }

  .sports-card {
    border: 1px solid var(--sports-border);
    border-radius: var(--sports-radius);
    overflow: hidden;
    background: var(--sports-card);
  }

  .sports-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.7rem 1rem;
    background: var(--sports-header);
    color: var(--sports-header-fg);
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  .sports-head small {
    color: var(--sports-header-fg);
    opacity: 0.75;
    font-weight: 400;
  }

  .sports-row {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--sports-border);
    font-size: 0.9rem;
  }

  .sports-row:last-child { border-bottom: 0; }

  .sports-row .away { text-align: right; }

  .sports-score {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    white-space: nowrap;
  }

  .sports-status {
    display: block;
    font-size: 0.72rem;
    font-weight: 400;
    color: var(--sports-muted);
    text-align: center;
  }

  .sports-status.live { color: var(--sports-live); font-weight: 600; }

  .sports-note {
    padding: 1rem;
    color: var(--sports-muted);
    font-size: 0.875rem;
  }

  .sports-note.error { color: var(--sports-live); }

  table.sports-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  table.sports-table th,
  table.sports-table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--sports-border);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  table.sports-table th:first-child,
  table.sports-table td:first-child {
    text-align: left;
    font-variant-numeric: normal;
  }

  table.sports-table th {
    color: var(--sports-muted);
    font-weight: 500;
  }

  @container (max-width: 380px) {
    .sports-row { font-size: 0.82rem; padding: 0.5rem 0.7rem; }
  }
`
